import React, { FC, useContext } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery } from '@apollo/react-hooks';
import {
  AnimatedScreen,
  FormActionFooter,
  FormInput,
  FormNavPrompt,
  LoadingPage,
  Table,
  useToast,
  HRule,
} from 'Shared/components';
import { useAuth0 } from 'Auth';
import { InfoPair } from 'Shared/components/Card/CardSectionInfoPairs/InfoPair';
import {
  INDEPENDENT_AGENT_STATES,
  VALID_US_STATES_TYPE,
} from 'Shared/constants/states';
import { updateStateCompliancePolicy } from 'Admin/authPolicies';
import { StateWithLabel } from 'Admin/components/StateWithLabel';
import {
  LicenseStatus,
  LicenseStatusType,
} from 'Admin/components/LicenseStatus';
import { PartnerContext } from 'Admin/utils';
import { NetworkError } from 'Admin/components/NetworkError';
import styles from './styles.module.css';
import {
  GET_PARTNER_COMPLIANCE,
  UPDATE_STATE_AUTHORIZATIONS,
} from 'Admin/Partners/graphql';

type AuthorizationToSellValues = Array<{
  state: VALID_US_STATES_TYPE;
  isAuthorized: boolean;
}>;

type PartnerComplianceType = {
  partner: {
    licensedAgency: {
      employerIdentificationNumber: string | null;
      nationalProducerNumber: string | null;
      authorizationsToSell: Array<{
        authorizedState: VALID_US_STATES_TYPE;
        isAuthorized: boolean;
      }>;
      licenses: Array<{
        state: VALID_US_STATES_TYPE;
        status: LicenseStatusType;
      }>;
      soleProprietor: boolean;
    } | null;
  } | null;
};

const agencyComplianceSchema = Yup.object().shape({
  authorizationToSell: Yup.array(),
});

export const Compliance: FC = () => {
  const { toast } = useToast();
  const { user } = useAuth0();
  const { currentPartner } = useContext(PartnerContext);

  const canEditStates =
    currentPartner &&
    updateStateCompliancePolicy(user!.effectiveGrants, {
      ancestorIds: currentPartner.ancestorIds,
    });

  const { data: complianceData, loading } = useQuery<PartnerComplianceType>(
    GET_PARTNER_COMPLIANCE,
    {
      variables: {
        partnerId: currentPartner?.id,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [updateStateAuthorizations, { error }] = useMutation<
    {},
    {
      partnerId: string;
      authorizationsToSell: Array<{
        state: VALID_US_STATES_TYPE;
        authorized: boolean;
      }>;
    }
  >(UPDATE_STATE_AUTHORIZATIONS, {
    errorPolicy: 'ignore',
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_PARTNER_COMPLIANCE,
        variables: {
          partnerId: currentPartner?.id,
        },
      },
    ],
  });

  if (!currentPartner) {
    return null;
  }

  if (loading) {
    return <LoadingPage />;
  }

  if (!complianceData) {
    return <NetworkError />;
  }

  const agency = {
    authorizationsToSell:
      complianceData.partner?.licensedAgency?.authorizationsToSell ?? [],
    licenses: complianceData.partner?.licensedAgency?.licenses ?? [],
    ein: complianceData.partner?.licensedAgency?.employerIdentificationNumber,
    npn: complianceData.partner?.licensedAgency?.nationalProducerNumber,
    soleProprietor: complianceData.partner?.licensedAgency?.soleProprietor,
  };

  // Show State Authorizations for any non-carrier partner: carrier=0 ancestors
  // (hidden), principal=1, brand=2, sub-agency≥2 (all shown). > 0 (not > 1) so a
  // nil-identity principal post sole-prop revert (AC02 — no NPN/EIN/sole-prop)
  // still surfaces the panel.
  const isNonCarrierPartner = (currentPartner.ancestorIds?.length ?? 0) > 0;
  const showStateAuthorizations =
    !!agency.ein ||
    !!agency.npn ||
    !!agency.soleProprietor ||
    isNonCarrierPartner;

  const hasLicenseStatuses = agency.licenses.length > 0;
  const displayLicenseStatuses =
    Boolean(agency.npn) &&
    !Boolean(agency.soleProprietor) &&
    hasLicenseStatuses;

  const initialFormValues = {
    authorizationsToSell: INDEPENDENT_AGENT_STATES.map((state) => {
      const currentState = agency.authorizationsToSell.find(
        (currentPartnerStateAuth) =>
          currentPartnerStateAuth.authorizedState === state
      );
      return {
        state,
        isAuthorized: currentState ? currentState.isAuthorized : false,
      };
    }),
  };

  const updateAuthorizations = async (
    authorizationsToSell: AuthorizationToSellValues
  ) => {
    await updateStateAuthorizations({
      variables: {
        partnerId: currentPartner.id,
        authorizationsToSell: authorizationsToSell.map(
          ({ state, isAuthorized }) => ({
            state: state,
            authorized: isAuthorized,
          })
        ),
      },
    });
  };

  return (
    <AnimatedScreen testId="compliance-details" className={styles.container}>
      <h1>Compliance</h1>
      <HRule size="lg" />
      <h3>Agency Information</h3>

      <InfoPair
        header="Agency National Producer Number"
        data={
          agency.soleProprietor ? 'Agency is a sole proprietor' : agency.npn
        }
        dataFontStyle="none"
      />

      {agency.ein && !agency.soleProprietor && (
        <InfoPair
          header="Employer Identification Number"
          data={agency.ein}
          dataFontStyle="none"
        />
      )}

      <Formik
        initialValues={initialFormValues}
        validationSchema={agencyComplianceSchema}
        validateOnBlur={true}
        enableReinitialize={true}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await updateAuthorizations(values.authorizationsToSell);
            toast({
              message: 'Page has been saved.',
              type: 'success',
              position: 'bottom',
              withSecondaryNav: true,
            });
          } catch {
            // Error is handled from useMutation hook
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          handleSubmit,
          setFieldValue,
          dirty,
          isValid,
          isSubmitting,
        }) => {
          return (
            <form onSubmit={handleSubmit}>
              <FormNavPrompt when={dirty} />
              {showStateAuthorizations && (
                <>
                  <HRule size="lg" />
                  <h3>State Authorizations</h3>
                  <p>
                    {displayLicenseStatuses && (
                      <span data-testid="doi-license-status-content">
                        D.O.I. License Status is based on the agency’s NPN
                        entered above. Agency state licenses must be active in a
                        non-revoked status and carry the correct LOA to be
                        permitted for quoting & servicing Clearcover insurance
                        products. If you believe a state license status is
                        incorrect, please confirm the NPN above is accurate and
                        contact the respective state D.O.I.&nbsp;
                      </span>
                    )}
                    To request an appointment in an additional state, visit the{' '}
                    <a
                      href="https://resources.clearcover.com/hc/en-us/articles/1500012376401-Applying-for-appointments-in-new-states"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.link}
                    >
                      Agent Hub
                    </a>
                    .
                  </p>
                  <Table
                    columnWidths={[
                      1,
                      displayLicenseStatuses ? 1 : 0,
                      '228px',
                    ].filter((x) => !!x)}
                    headers={[
                      'State',
                      displayLicenseStatuses && 'D.O.I. License Status',
                      'Agency can quote & service?',
                    ].filter((x) => !!x)}
                    rows={values.authorizationsToSell.map(
                      ({ state, isAuthorized }, idx) => {
                        const licenseStatus = agency.licenses.find(
                          (record) => record.state === state
                        )?.status;
                        return {
                          key: state,
                          className: styles.stateRow,
                          data: [
                            <StateWithLabel state={state} />,
                            displayLicenseStatuses &&
                              (licenseStatus ? (
                                <LicenseStatus status={licenseStatus} />
                              ) : null),
                            <div className={styles.switch}>
                              <FormInput
                                data-testid={`state-authorization-${state}`}
                                className={styles.radioButtonGroup}
                                variant="radioButtonGroup"
                                name={`state-authorization-${state}`}
                                value={isAuthorized ? 'yes' : 'no'}
                                disabled={!canEditStates}
                                label={`Authorized in ${state}?`}
                                hideLabel
                                sizeVariant="small"
                                onChange={(newValue: string) => {
                                  setFieldValue(
                                    `authorizationsToSell[${idx}]`,
                                    {
                                      state,
                                      isAuthorized: newValue === 'yes',
                                    }
                                  );
                                }}
                                options={{
                                  no: 'No',
                                  yes: 'Yes',
                                }}
                              />
                            </div>,
                          ].filter((x) => !!x),
                        };
                      }
                    )}
                  />
                </>
              )}

              <FormActionFooter
                error={error?.message}
                disabled={!dirty || !isValid}
                isSubmitting={isSubmitting}
              />
            </form>
          );
        }}
      </Formik>
    </AnimatedScreen>
  );
};
